/**
 * Extracted from CheckoutPage.jsx — three small but reused UI
 * primitives + the address picker drawer.
 *
 * ``AddressPickerDrawer`` here is intentionally separate from the
 * one in ``CartPickers`` because Checkout uses label-icon mapping
 * keyed by the canonical case ("Home" / "Work" / "Other") whereas
 * Cart uses lowercase labels. The two flows could be merged later,
 * but it'd require a small data-shape migration of the addresses
 * stored on Cart vs Checkout.
 */
import { MapPin, Home, Briefcase, Plus } from "lucide-react";

const LABEL_ICONS = { Home, Work: Briefcase, Other: MapPin };

export function AddrSummary({ a }) {
    const Icon = LABEL_ICONS[a.label] || MapPin;
    return (
        <div className="flex items-start gap-3" data-testid="selected-address">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0"><Icon size={18} /></div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <p className="font-bold text-sm">{a.label}</p>
                    {a.is_default && <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Default</span>}
                </div>
                <p className="text-sm mt-0.5 line-clamp-2">{a.line1}{a.line2 ? `, ${a.line2}` : ""}{a.landmark ? `, near ${a.landmark}` : ""}</p>
                <p className="text-xs text-muted-foreground">{[a.city, a.state, a.pincode].filter(Boolean).join(", ")}</p>
            </div>
        </div>
    );
}

export function PayOpt({ id, current, setPayment, Icon, label, sub, disabled }) {
    const sel = current === id;
    return (
        <button
            data-testid={`pay-${id}`}
            onClick={() => !disabled && setPayment(id)}
            disabled={disabled}
            className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border text-left disabled:opacity-50 disabled:cursor-not-allowed ${sel ? "border-primary bg-primary/5" : "border-border"}`}
        >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${sel ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                <Icon size={18} />
            </div>
            <div className="flex-1">
                <p className="text-sm font-semibold">{label}</p>
                <p className="text-xs text-muted-foreground">{sub}</p>
            </div>
            <span className={`w-5 h-5 rounded-full border-2 ${sel ? "border-primary bg-primary" : "border-border"}`} />
        </button>
    );
}

export function Line({ k, v }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{k}</span>
            <span>{v}</span>
        </div>
    );
}

export function CheckoutAddressDrawer({
    addrList, selectedAddrId, onPick, onAddNew, onClose,
}) {
    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" data-testid="address-picker">
            <div className="bg-card w-full max-w-md md:max-w-lg rounded-t-3xl sm:rounded-3xl p-5 max-h-[85vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-lg">Choose address</h3>
                    <button onClick={onClose} className="text-sm font-semibold text-muted-foreground">Close</button>
                </div>
                <div className="space-y-2.5">
                    {addrList.map((a) => {
                        const sel = a.id === selectedAddrId;
                        const Icon = LABEL_ICONS[a.label] || MapPin;
                        return (
                            <button
                                key={a.id}
                                data-testid={`picker-addr-${a.id}`}
                                onClick={() => onPick(a.id)}
                                className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left ${sel ? "border-primary bg-primary/5" : "border-border"}`}
                            >
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${sel ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}><Icon size={16} /></div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm">{a.label}{a.is_default ? " • Default" : ""}</p>
                                    <p className="text-xs text-muted-foreground line-clamp-2">{a.line1}{a.line2 ? `, ${a.line2}` : ""}{a.pincode ? ` - ${a.pincode}` : ""}</p>
                                </div>
                                <span className={`w-5 h-5 rounded-full border-2 mt-1 flex-shrink-0 ${sel ? "border-primary bg-primary" : "border-border"}`} />
                            </button>
                        );
                    })}
                </div>
                <button
                    data-testid="picker-add-new"
                    onClick={onAddNew}
                    className="mt-4 w-full border border-dashed border-primary/40 text-primary rounded-2xl py-3 font-semibold inline-flex items-center justify-center gap-2"
                >
                    <Plus size={16} /> Add new address
                </button>
            </div>
        </div>
    );
}
